import { useAuthDetails } from "@/context/auth-context";
import { useUserGroupsData } from "@/data/use-groups-data";
import { getEnvironmentVariable } from "@/lib/environment";
import {
  Identify as AmplitudeIdentify,
  identify as amplitudeIdentify,
  init as amplitudeInit,
  track as amplitudeTrack,
} from "@amplitude/analytics-browser";
import { createContext, useCallback, useContext, useEffect } from "react";
import { EventProperties } from "../types/event-types";
import { useGroupSlug } from "./use-group-slug";

const AMPLITUDE_API_KEY = getEnvironmentVariable("AMPLITUDE_API_KEY", false);

/**
 * Following the naming conventions from https://amplitude.com/explore/data/event-taxonomy
 */
type Track = (
  /**
   * Convention: object_action (e.g. "button_clicked")
   */
  name: string,
  /** Example: { property_name: "property_value" } */
  properties?: EventProperties,
) => void;

// ============================================================
// Tracker
// ============================================================

const TrackerContext = createContext({
  track: (() => {
    throw new Error("useTracker() must be used within a TrackerProvider");
  }) as Track,
});

export const TrackerProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const authDetails = useAuthDetails();
  const groupSlug = useGroupSlug(false);
  const { groups } = useUserGroupsData();
  const organizationUuid =
    groups.find((group) => group.slug === groupSlug)?.organizationUuid ??
    "unknown";

  useEffect(() => {
    if (AMPLITUDE_API_KEY === null) {
      return;
    }

    amplitudeInit(AMPLITUDE_API_KEY, {
      autocapture: {
        pageViews: false,
      },
    });
  }, []);

  useEffect(() => {
    if (AMPLITUDE_API_KEY === null) {
      return;
    }

    if (authDetails) {
      const identity = new AmplitudeIdentify();
      identity.set("name", authDetails.name);
      identity.set("email", authDetails.email);
      identity.set("last_group", groupSlug ?? "unknown");
      identity.set("last_organization_uuid", organizationUuid);
      amplitudeIdentify(identity, {
        user_id: authDetails.email,
      });
    }
  }, [authDetails, groupSlug, organizationUuid]);

  const track: Track = useCallback(
    (name, properties) => {
      const finalProperties = {
        page_domain: window.location.hostname,
        page_location: window.location.toString(),
        page_path: window.location.pathname,
        page_title: document.title || "unknown",
        page_url: window.location.href,
        group_slug: groupSlug || "unknown",
        organization_uuid: organizationUuid,
        ...properties,
      };
      if (AMPLITUDE_API_KEY) {
        amplitudeTrack(name, finalProperties);
      } else {
        console.log("Track: ", name, finalProperties);
      }
    },
    [authDetails, groupSlug, organizationUuid],
  );

  return (
    <TrackerContext.Provider value={{ track }}>
      {children}
    </TrackerContext.Provider>
  );
};

export const useTracker = () => useContext(TrackerContext);
